import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { formatFechaSoloDiaDdMmYyyy } from './formatFechaSoloDia';

const EMPLEADOS_POR_PDF = 25;

function formatCurrency(amount) {
  const value = Number(amount);
  if (Number.isNaN(value)) return 'S/ 0.00';
  return new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(value);
}

function escapeHtml(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Fecha habitual de pago: 15/07 (Fiestas Patrias) o 15/12 (Navidad). */
export function fechaPagoGratificacionYmd(gratificacion) {
  const anio = Number(gratificacion?.anio);
  if (!anio || Number.isNaN(anio)) return null;
  const p = String(gratificacion?.periodo || '').toLowerCase();
  if (p === 'junio') return `${anio}-07-15`;
  if (p === 'diciembre') return `${anio}-12-15`;
  const nombre = String(gratificacion?.periodo_nombre || '').toLowerCase();
  if (nombre.includes('junio') || nombre.includes('fiestas')) return `${anio}-07-15`;
  if (nombre.includes('dici') || nombre.includes('navidad')) return `${anio}-12-15`;
  return null;
}

function fechaLargaEs(fechaYmd) {
  const f = formatFechaSoloDiaDdMmYyyy(fechaYmd, '');
  if (!f) return '';
  const [d, m, y] = f.split('/');
  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
  ];
  const mi = parseInt(m, 10) - 1;
  if (mi < 0 || mi > 11) return f;
  return `${parseInt(d, 10)} de ${meses[mi]} del ${y}`;
}

function bloqueSeccion(titulo, contenido, esTotal = false) {
  const estilo = esTotal
    ? 'background: #dbeafe; border: 1px solid #3b82f6;'
    : 'background: #f9fafb; border: 1px solid #e5e7eb;';
  const estiloTitulo = esTotal ? 'color: #1e40af;' : 'color: #1f2937;';
  return `
    <div style="margin-bottom: 12px; padding: 10px; border-radius: 6px; ${estilo}">
      <h4 style="margin: 0 0 8px 0; font-size: 12px; font-weight: bold; ${estiloTitulo}">${titulo}</h4>
      ${contenido}
    </div>`;
}

const estiloCelda =
  'padding: 5px 8px; border: 1px solid #e5e7eb; font-size: 10px; color: #374151;';
const estiloCeldaLabel = `${estiloCelda} font-weight: bold; background: #f3f4f6; color: #1f2937;`;

function contarMesesComputables(detalleRow) {
  if (detalleRow?.meses_computables != null && Number(detalleRow.meses_computables) > 0) {
    return Number(detalleRow.meses_computables);
  }
  const sueldos = Array.isArray(detalleRow?.sueldos_mensuales) ? detalleRow.sueldos_mensuales : [];
  const conMonto = sueldos.filter(
    (s) => !s.excluido_del_calculo && (Number(s.monto) || 0) > 0
  );
  return conMonto.length;
}

export function buildHtmlConstanciaPagoGratificacion({
  empresa,
  gratificacion,
  detalleRow,
  firmaEmpresaBase64,
  logoEmpresaBase64,
  opciones = {},
}) {
  const nombreEmpresa = empresa?.nombre || empresa?.name || empresa?.nombre_empresa || 'EMPRESA';
  const ruc = empresa?.ruc || '—';
  const direccion = empresa?.direccion || empresa?.address || '';
  const periodoLabel =
    gratificacion?.periodo_nombre ||
    (gratificacion?.periodo === 'junio' ? 'Junio' : 'Diciembre');
  const anio = gratificacion?.anio || '';
  const esJunio = String(gratificacion?.periodo || '').toLowerCase() === 'junio'
    || String(periodoLabel).toLowerCase().includes('junio');
  const motivo = esJunio ? 'Fiestas Patrias' : 'Navidad';

  const base = Number(detalleRow?.base_remuneracion ?? detalleRow?.promedio6Meses) || 0;
  const bruta = Number(detalleRow?.gratificacionBruta) || 0;
  const bonif = Number(detalleRow?.bonificacion) || 0;
  const total = Number(detalleRow?.gratificacionNeta) || bruta + bonif;
  const meses = contarMesesComputables(detalleRow);
  const esFija = String(detalleRow?.tipo_remuneracion || '').toLowerCase() === 'fija';
  const etiquetaBase = esFija ? 'Remuneración fija (base)' : 'Promedio de remuneraciones (base)';

  const fIng = formatFechaSoloDiaDdMmYyyy(detalleRow?.ultima_fecha_ingreso_labores, '—');
  const fIni = formatFechaSoloDiaDdMmYyyy(gratificacion?.fecha_inicio, '—');
  const fFin = formatFechaSoloDiaDdMmYyyy(gratificacion?.fecha_fin, '—');
  const fechaPagoYmd = opciones.fechaPago || fechaPagoGratificacionYmd(gratificacion);
  const fPago = fechaPagoYmd ? fechaLargaEs(fechaPagoYmd) : '—';
  const representante = opciones.representanteLegal || 'Representante Legal';
  const regimen = escapeHtml((detalleRow?.regimen || detalleRow?.regimen_codigo || 'GENERAL').toUpperCase());
  const codigoTrab = escapeHtml(
    detalleRow?.codigo_trabajador || String(detalleRow?.id || '').padStart(8, '0')
  );

  const banco = (detalleRow?.banco || '').trim();
  const cuenta = (detalleRow?.cuenta_bancaria || '').trim();

  const logoHtml = logoEmpresaBase64
    ? `<img src="${logoEmpresaBase64}" alt="Logo" style="max-height: 72px; max-width: 150px; object-fit: contain; display: block;" />`
    : '';

  const tablaDatosTrabajador = `
    <table style="width: 100%; border-collapse: collapse;">
      <tr>
        <td style="${estiloCeldaLabel} width: 22%;">RÉGIMEN LABORAL</td>
        <td style="${estiloCelda}">${regimen}</td>
        <td style="${estiloCeldaLabel} width: 22%;">CÓDIGO TRABAJADOR</td>
        <td style="${estiloCelda}">${codigoTrab}</td>
      </tr>
      <tr>
        <td style="${estiloCeldaLabel}">NOMBRES Y APELLIDOS</td>
        <td colspan="3" style="${estiloCelda}">${escapeHtml(detalleRow?.empleado || '')}</td>
      </tr>
      <tr>
        <td style="${estiloCeldaLabel}">DNI / CARNET EXTRANJ.</td>
        <td style="${estiloCelda}">${escapeHtml(detalleRow?.documento || '—')}</td>
        <td style="${estiloCeldaLabel}">PUESTO</td>
        <td style="${estiloCelda}">${escapeHtml(detalleRow?.cargo || '—')}</td>
      </tr>
      <tr>
        <td style="${estiloCeldaLabel}">FECHA DE INGRESO</td>
        <td colspan="3" style="${estiloCelda}">${escapeHtml(fIng)}</td>
      </tr>
    </table>`;

  const tablaDatosBancarios =
    banco || cuenta
      ? `
    <table style="width: 100%; border-collapse: collapse; margin-top: 8px;">
      ${
        banco
          ? `<tr>
        <td style="${estiloCeldaLabel} width: 22%;">BANCO</td>
        <td colspan="3" style="${estiloCelda}">${escapeHtml(banco)}</td>
      </tr>`
          : ''
      }
      ${
        cuenta
          ? `<tr>
        <td style="${estiloCeldaLabel}">CUENTA BANCARIA</td>
        <td colspan="3" style="${estiloCelda}; font-weight: bold;">${escapeHtml(cuenta)}</td>
      </tr>`
          : ''
      }
    </table>`
      : '';

  const cuentaTexto = cuenta
    ? ` en la cuenta N° <strong style="color: #1f2937;">${escapeHtml(cuenta)}</strong>${banco ? ` del <strong style="color: #1f2937;">${escapeHtml(banco)}</strong>` : ''}`
    : '';

  const parrafoPago = `
    <p style="margin: 0; font-size: 10px; color: #4b5563; line-height: 1.45; text-align: justify;">
      La empresa <strong style="color: #1f2937;">${escapeHtml(nombreEmpresa)}</strong>, con R.U.C. N°
      <strong style="color: #1f2937;">${escapeHtml(ruc)}</strong>, representada por
      <strong style="color: #1f2937;">${escapeHtml(representante)}</strong>, deja constancia del pago de la
      gratificación ordinaria por <strong style="color: #1f2937;">${escapeHtml(motivo)}</strong>
      correspondiente al semestre <strong style="color: #1f2937;">${escapeHtml(periodoLabel)} ${escapeHtml(String(anio))}</strong>
      a favor del trabajador${cuentaTexto}, con fecha
      <strong style="color: #1f2937;">${escapeHtml(fPago)}</strong>, conforme a la Ley N° 27735 y normas conexas.
    </p>`;

  const tablaPeriodo = `
    <table style="width: 100%; border-collapse: collapse;">
      <tr>
        <td style="${estiloCelda} text-align: center; font-weight: bold; color: #1f2937;">
          DEL ${escapeHtml(fIni)} AL ${escapeHtml(fFin)}
        </td>
      </tr>
      <tr>
        <td style="${estiloCelda} text-align: center;">
          <strong>${meses}</strong> MESES COMPUTABLES &nbsp;|&nbsp; Proporcional: <strong>${meses}</strong> / 6
        </td>
      </tr>
    </table>`;

  const tablaRemuneracion = `
    <table style="width: 100%; border-collapse: collapse;">
      <tr>
        <td style="${estiloCelda}">${escapeHtml(etiquetaBase)}</td>
        <td style="${estiloCelda} text-align: right;">${formatCurrency(base)}</td>
      </tr>
      <tr style="background: #eff6ff;">
        <td style="${estiloCelda} font-weight: bold; color: #1e40af;">BASE PARA GRATIFICACIÓN</td>
        <td style="${estiloCelda} text-align: right; font-weight: bold; color: #1e40af;">${formatCurrency(base)}</td>
      </tr>
    </table>`;

  const tablaCalculo = `
    <table style="width: 100%; border-collapse: collapse;">
      <thead>
        <tr style="background: #f3f4f6;">
          <th style="${estiloCeldaLabel}">CONCEPTO</th>
          <th style="${estiloCeldaLabel} text-align: center;">DETALLE</th>
          <th style="${estiloCeldaLabel} text-align: right;">MONTO</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style="${estiloCelda}">Gratificación bruta</td>
          <td style="${estiloCelda} text-align: center;">Base × (${meses}/6)</td>
          <td style="${estiloCelda} text-align: right; font-weight: bold;">${formatCurrency(bruta)}</td>
        </tr>
        <tr>
          <td style="${estiloCelda}">Bonificación extraordinaria (9%)</td>
          <td style="${estiloCelda} text-align: center;">Ley 29351 / 30334</td>
          <td style="${estiloCelda} text-align: right; font-weight: bold;">${formatCurrency(bonif)}</td>
        </tr>
      </tbody>
    </table>`;

  const firmaEmpleador = firmaEmpresaBase64
    ? `<img src="${firmaEmpresaBase64}" style="width: 180px; height: auto; object-fit: contain;" alt="Firma del Empleador" />`
    : '<p style="font-size: 10px; color: #6b7280;">Espacio para firma del empleador</p>';

  const fechaGeneracion = new Date().toLocaleDateString('es-PE');

  return `
    <div style="padding: 25px; background: white; border: 3px solid #1f2937; border-radius: 12px; box-shadow: 0 4px 8px rgba(0,0,0,0.15); font-family: Arial, sans-serif; color: #111; line-height: 1.35; box-sizing: border-box;">
      <div style="position: relative; text-align: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px solid #e5e7eb; min-height: 88px;">
        ${
          logoHtml
            ? `<div style="position: absolute; left: 0; top: 0; width: 150px; text-align: left;">${logoHtml}</div>`
            : ''
        }
        <h1 style="color: #1f2937; margin: 0 0 8px 0; font-size: 24px; font-weight: bold;">${escapeHtml(nombreEmpresa)}</h1>
        <p style="color: #6b7280; font-size: 14px; margin: 5px 0;">RUC: ${escapeHtml(ruc)}</p>
        ${direccion ? `<p style="color: #6b7280; font-size: 12px; margin: 4px 0;">${escapeHtml(direccion)}</p>` : ''}
        <h2 style="color: #047857; font-size: 18px; margin: 12px 0 4px 0; font-weight: bold;">CONSTANCIA DE PAGO DE GRATIFICACIÓN</h2>
        <p style="color: #6b7280; font-size: 12px; margin: 0;">Ley N° 27735 — ${escapeHtml(motivo)} — ${escapeHtml(periodoLabel)} ${escapeHtml(String(anio))}</p>
      </div>

      <div style="margin-bottom: 16px;">
        <h3 style="color: #374151; font-size: 14px; font-weight: bold; margin: 0 0 10px 0;">DATOS DEL TRABAJADOR</h3>
        ${tablaDatosTrabajador}
        ${
          tablaDatosBancarios
            ? `<h3 style="color: #374151; font-size: 14px; font-weight: bold; margin: 14px 0 10px 0;">DATOS BANCARIOS</h3>${tablaDatosBancarios}`
            : ''
        }
      </div>

      ${bloqueSeccion('DECLARACIÓN DE PAGO', parrafoPago)}
      ${bloqueSeccion('1.- PERÍODO COMPUTABLE', tablaPeriodo)}
      ${bloqueSeccion('2.- REMUNERACIÓN BASE', tablaRemuneracion)}
      ${bloqueSeccion('3.- CÁLCULO DE LA GRATIFICACIÓN', tablaCalculo)}
      ${bloqueSeccion(`TOTAL A PAGAR — ${formatCurrency(total)}`, '', true)}

      <p style="text-align: center; margin: 16px 0 8px 0; font-size: 12px; color: #374151;">
        ${escapeHtml(fPago)}
      </p>

      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-top: 20px;">
        <div style="text-align: center; background: #f9fafb; padding: 20px; border-radius: 10px; border: 2px solid #e5e7eb;">
          <div style="height: 60px; margin-bottom: 15px; display: flex; align-items: center; justify-content: center;">
            ${firmaEmpleador}
          </div>
          <p style="margin: 0; font-size: 12px; font-weight: bold; color: #374151;">FIRMA DEL EMPLEADOR</p>
        </div>
        <div style="text-align: center; background: #f9fafb; padding: 20px; border-radius: 10px; border: 2px solid #e5e7eb;">
          <div style="height: 60px; margin-bottom: 15px; border-bottom: 2px solid #374151;"></div>
          <p style="margin: 0; font-size: 12px; font-weight: bold; color: #374151;">FIRMA DEL TRABAJADOR</p>
          <p style="margin: 5px 0 0; font-size: 11px; color: #6b7280;">${escapeHtml(detalleRow?.empleado || '')}</p>
        </div>
      </div>

      <div style="text-align: center; color: #6b7280; font-size: 10px; padding-top: 15px; margin-top: 20px; border-top: 1px solid #e5e7eb;">
        <p style="margin: 3px 0;">Fecha de generación: ${fechaGeneracion}</p>
        <p style="margin: 3px 0;">Sistema de Planillas - ${escapeHtml(nombreEmpresa)}</p>
      </div>
    </div>`;
}

async function agregarHtmlAPdf(pdf, html, esPrimeraPagina) {
  const contenedor = document.createElement('div');
  contenedor.style.cssText =
    'position:absolute;left:-9999px;top:0;width:900px;background:#fff;padding:20px;font-family:Arial,sans-serif;';
  contenedor.innerHTML = html;
  document.body.appendChild(contenedor);
  const canvas = await html2canvas(contenedor, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false,
  });
  document.body.removeChild(contenedor);
  if (!esPrimeraPagina) pdf.addPage();
  const imgData = canvas.toDataURL('image/png', 1.0);
  const imgWidth = 210;
  let imgHeight = (canvas.height * imgWidth) / canvas.width;
  if (imgHeight > 297) imgHeight = 297;
  pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
}

export async function descargarConstanciaGratificacionIndividual(params) {
  const html = buildHtmlConstanciaPagoGratificacion(params);
  const pdf = new jsPDF('p', 'mm', 'a4');
  await agregarHtmlAPdf(pdf, html, true);
  const emp = (params.detalleRow?.empleado || 'empleado').replace(/\s+/g, '_').slice(0, 40);
  pdf.save(`Constancia_Gratificacion_${emp}_${new Date().toISOString().split('T')[0]}.pdf`);
}

export async function exportarTodasConstanciasGratificacion({
  empresa,
  gratificacion,
  detalle,
  firmaEmpresaBase64,
  logoEmpresaBase64,
  opciones = {},
}) {
  const lista = (detalle || []).filter((r) => (Number(r.gratificacionNeta) || 0) > 0);
  if (!lista.length) {
    throw new Error('No hay trabajadores con total de gratificación para generar constancias.');
  }

  const total = lista.length;
  const totalPDFs = Math.ceil(total / EMPLEADOS_POR_PDF);
  const modal = document.createElement('div');
  modal.id = 'modal-progreso-constancias-grat';
  modal.style.cssText =
    'position:fixed;inset:0;background:rgba(0,0,0,0.75);z-index:9999;display:flex;align-items:center;justify-content:center;';
  modal.innerHTML =
    '<div style="background:#fff;padding:32px;border-radius:12px;min-width:380px;text-align:center;">' +
    '<h3 style="margin:0 0 8px;color:#047857;">Generando constancias de gratificación</h3>' +
    '<p style="font-size:13px;color:#6b7280;">' +
    total +
    ' trabajador(es)</p>' +
    '<div id="barra-progreso-const-grat" style="background:#047857;height:22px;border-radius:6px;width:0%;margin:16px 0;color:#fff;font-size:11px;line-height:22px;">0%</div>' +
    '<p id="nombre-empleado-const-grat" style="font-size:13px;font-weight:600;"></p></div>';
  document.body.appendChild(modal);

  const periodoSlug = (gratificacion?.periodo_nombre || gratificacion?.periodo || 'Grat').replace(/\s+/g, '_');
  const fecha = new Date().toISOString().split('T')[0];
  const archivos = [];

  try {
    for (let pdfIndex = 0; pdfIndex < totalPDFs; pdfIndex += 1) {
      const lote = lista.slice(pdfIndex * EMPLEADOS_POR_PDF, (pdfIndex + 1) * EMPLEADOS_POR_PDF);
      const pdf = new jsPDF('p', 'mm', 'a4');
      for (let i = 0; i < lote.length; i += 1) {
        const row = lote[i];
        const idx = pdfIndex * EMPLEADOS_POR_PDF + i;
        const pct = Math.round(((idx + 1) / total) * 100);
        const barra = document.getElementById('barra-progreso-const-grat');
        if (barra) {
          barra.style.width = `${pct}%`;
          barra.textContent = `${pct}%`;
        }
        const nombreEl = document.getElementById('nombre-empleado-const-grat');
        if (nombreEl) nombreEl.textContent = row.empleado || '';
        const html = buildHtmlConstanciaPagoGratificacion({
          empresa,
          gratificacion,
          detalleRow: row,
          firmaEmpresaBase64,
          logoEmpresaBase64,
          opciones,
        });
        await agregarHtmlAPdf(pdf, html, i === 0);
        await new Promise((r) => setTimeout(r, 80));
      }
      const nombre =
        totalPDFs === 1
          ? `Constancias_Gratificacion_${periodoSlug}_${fecha}.pdf`
          : `Constancias_Gratificacion_${periodoSlug}_Parte${pdfIndex + 1}de${totalPDFs}_${fecha}.pdf`;
      pdf.save(nombre);
      archivos.push(nombre);
    }
    return archivos;
  } finally {
    if (modal.parentNode) modal.parentNode.removeChild(modal);
  }
}
